import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { TeamProvider } from "@/contexts/TeamContext";
import { TagsProvider } from "@/contexts/TagsContext";
import { FamilyProvider } from "@/contexts/FamilyContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { AuthGate } from "@/components/AuthGate";
import { AuthQueryCacheBuster } from "@/components/AuthQueryCacheBuster";
import { toast } from "@/lib/toast";
import { useErrorLogging } from "@/hooks/useErrorLogging";
import { EnvironmentThemeHandler } from "@/components/EnvironmentThemeHandler";
import { TaskEventHandler } from "./components/TaskEventHandler";
import { StockEventHandler } from "./components/StockEventHandler";
import { TeamThemeHandler } from "./components/TeamThemeHandler";
import { AppLoader } from "./components/AppLoader";
import { DiscoveryProvider } from "@/contexts/DiscoveryContext";
import { AppUpdateManager } from "./components/AppUpdateManager";
import { AppUsageTracker } from "./components/AppUsageTracker";
import { Analytics } from "@vercel/analytics/react";
import { ToastSettingsManager } from "./components/ToastSettingsManager";
import { ErrorContextManager } from "./components/ErrorContextManager";
import { NetworkStatusBanner } from "./components/NetworkStatusBanner";


// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const InstallPage = lazy(() => import("./pages/InstallPage"));
const AdminPageComp = lazy(() => import("./pages/AdminPage"));
const AuthorizeDevicePage = lazy(() => import("./pages/AuthorizeDevicePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Integrations = lazy(() => import("./pages/Integrations"));
const WebhookHandler = lazy(() => import("./pages/WebhookHandler"));
const SuperAdminUsersView = lazy(() => import("./components/SuperAdminUsersView").then(m => ({ default: m.SuperAdminUsersView })));

// Lazy load handlers
const TaskActionHandler = lazy(() => import("./components/TaskActionHandler"));
const TaskCreateHandler = lazy(() => import("./components/TaskCreateHandler"));

const queryClient = new QueryClient();

const App = () => {
  // Initialize error logging (captures global errors and syncs to backend)
  useErrorLogging();

  // Listen for app update event
  useEffect(() => {
    const handleAppUpdated = () => {
      toast.success("App bijgewerkt! 🎉", {
        channel: 'general',
        description: "De nieuwste versie is geïnstalleerd.",
        duration: 4000,
      });
    };

    window.addEventListener('app-updated', handleAppUpdated);
    return () => window.removeEventListener('app-updated', handleAppUpdated);
  }, []);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <SettingsProvider>
        <EnvironmentThemeHandler />
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AuthProvider>
              <AppUsageTracker />
              <AuthQueryCacheBuster />
              <TeamProvider>
                <ErrorContextManager />
                <TeamThemeHandler />
                <TagsProvider>
                  <FamilyProvider>
                    <DiscoveryProvider>
                      <Toaster />
                      <Sonner />
                      <ToastSettingsManager />
                      <AppUpdateManager />
                      <TaskEventHandler />
                      <StockEventHandler />
                      <NetworkStatusBanner />

                      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <Suspense fallback={<AppLoader isLoading={true} minDisplayTime={0} />}>
                          <Routes>
                            {/* Protected routes - require auth in production */}
                            <Route path="/" element={<AuthGate><Index /></AuthGate>} />
                            <Route path="/admin" element={<AuthGate><AdminPageComp /></AuthGate>} />
                            <Route path="/:teamSlug" element={<AuthGate><TeamPage /></AuthGate>} />

                            {/* Public routes - always accessible */}
                            <Route path="/install" element={<InstallPage />} />
                            <Route path="/authorize-device" element={<AuthorizeDevicePage />} />
                            <Route path="/webhook/:slug" element={<WebhookHandler />} />
                            <Route path="/admin/users" element={<AuthGate><SuperAdminUsersView /></AuthGate>} />

                            {/* API/Action Routes - require auth */}
                            <Route path="/api/tasks/:taskId/:action" element={<AuthGate><TaskActionHandler /></AuthGate>} />
                            <Route path="/api/create-task" element={<AuthGate><TaskCreateHandler /></AuthGate>} />

                            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                      </BrowserRouter>
                      <Analytics />
                    </DiscoveryProvider>
                  </FamilyProvider>
                </TagsProvider>
              </TeamProvider>
            </AuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
};

export default App;
